import React, { useState, useRef, useContext, useEffect }  from 'react'
import { View, StyleSheet, ScrollView, KeyboardAvoidingView, TouchableOpacity } from 'react-native'
import { Button, Text, Spinner, Modal, Icon, Datepicker, Toggle, useTheme } from '@ui-kitten/components'
import * as Sentry from "@sentry/react-native"
import {
  resetCollectionData as resetCollectionDataFn,
  resetMovementData as resetMovementDataFn,
  resetProductStocks as resetProductStocksFn,
  deleteAllProducts as deleteAllProductsFn
} from '../../global/Firebase/firebase'
import { organizationsApi } from '../../global/API/typedApiClient'
import { assertWWriterAvailable } from '../../global/Write/writeW'
import { useTranslation } from 'react-i18next'
import { useAtomValue } from 'jotai'
import { organizationAtom, productsDictAtom, localeDateServiceAtom } from '../../global/AtomsProvider'
import { UserContext } from '../../global/UserProvider'
import HeaderIconButton from '@/components/HeaderIconButton'
import { format } from 'date-fns'

// Collections to delete data from before the selected date
const COLLECTIONS_TO_RESET = [
  { collection: 'sales', dateField: 'saleDate' },
  { collection: 'complexSales', dateField: 'saleDate' },
  { collection: 'supplies', dateField: 'supplyDate' },
  { collection: 'writeOffs', dateField: 'date' },
  { collection: 'transfers', dateField: 'date' },
  { collection: 'spendings', dateField: 'date' },
  { collection: 'revisions', dateField: 'date' },
  { collection: 'manufactures', dateField: 'date' },
  { collection: 'dailyStats', dateField: 'date' },
  { collection: 'monthlyStats', dateField: 'date' },
  { collection: 'dailyProductReports', dateField: 'date' },
  { collection: 'transactions', dateField: 'date' },
  { collection: 'productStockSnapshots', dateField: 'date' },
  { collection: 'orders', dateField: 'date' }
]


export default function ResetDataScreen( { navigation } ) {
  const { t } = useTranslation()
  const [context] = useContext(UserContext)
  const organization = useAtomValue(organizationAtom)
  const productsDict = useAtomValue(productsDictAtom)
  const localeDateService = useAtomValue(localeDateServiceAtom)
  const _datePicker = useRef(null)
  const db = context.db
  const theme = useTheme()

  const [saveError, setSaveError] = useState('')
  const [successMessageVisible, setSuccessMessageVisible] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  
  // State for reset operations
  const [resetDate, setResetDate] = useState(new Date())
  const [confirmationModalVisible, setConfirmationModalVisible] = useState(false)
  const [shouldResetProductStocks, setShouldResetProductStocks] = useState(false)
  const [deleteProducts, setDeleteProducts] = useState(false)

  useEffect(() => {
    if (navigation.getState()?.index === 0) {
      navigation.setOptions({
        headerLeft: () => (
          <HeaderIconButton
            onPress={() => {
              navigation.navigate('Home')
            }}
            iconName='arrow-back-outline'
          />
        ),
      })
    }
  }, [])

  async function executeResetOperations() {
    // Check if user is admin
    if (context.user.role !== 'admin') {
      setSaveError(t('organizationDetails.adminRequiredError'))
      return
    }

    setIsLoading(true)
    setSaveError('')

    try {
      // ATL-1445 P9b: unconverted W writer — refuses in a staging cut-over preview's Supabase mode
      // (`assertWWriterAvailable`); a no-op in every production build.
      assertWWriterAvailable('screens/Settings/ResetDataScreen.js#ResetDataScreen')
      // Create cutoff date - delete all data before this date
      const cutoffDate = new Date(resetDate)
      cutoffDate.setHours(0, 0, 0, 0)

      // Delete data from all collections before the selected date using centralized function
      for (const collectionConfig of COLLECTIONS_TO_RESET) {
        await resetCollectionDataFn(db, collectionConfig.collection, collectionConfig.dateField, cutoffDate)
      }

      // Delete movement collection group using centralized function
      await resetMovementDataFn(db, organization.id, cutoffDate)

      // Reset product stocks if enabled
      if (shouldResetProductStocks) {
        await resetProductStocksFn(productsDict)
      }

      // Delete products if enabled
      if (deleteProducts) {
        await deleteAllProductsFn(db)
      }

      // Supabase dual write - single API call to delete matching data
      await organizationsApi.resetData({
        cutoffDate: cutoffDate.toISOString(),
        resetProductStocks: shouldResetProductStocks,
        deleteAllProducts: deleteProducts,
      })

      setSuccessMessageVisible(true)
      setIsLoading(false)
    } catch (error) {
      Sentry.captureException(error)
      console.log(error)
      setSaveError(t('error'))
      setIsLoading(false)
    }
  }

  return (
    <KeyboardAvoidingView style={styles.keyboardAvoidingView} behavior="height" keyboardVerticalOffset = {100}>
      <ScrollView style={styles.container} keyboardShouldPersistTaps='handled' keyboardDismissMode='on-drag'>
        
        <Text category='h3' style={styles.title}>
          {t('organizationDetails.resetData')}
        </Text>
        <Text appearance='hint' style={styles.description}>
          {t('organizationDetails.resetDataDescription')}
        </Text>
        
        {context.user.role !== 'admin' && (
          <View style={styles.adminRequiredCard}>
            <Text category='h6' style={styles.adminRequiredTitle}>
              🔒 {t('organizationDetails.adminRequired')}
            </Text>
            <Text style={styles.adminRequiredText}>
              {t('organizationDetails.adminRequiredDescription')}
            </Text>
          </View>
        )}

        <Text category='label' style={styles.dateLabel}>
          {t('organizationDetails.resetDataBeforeDate')}
        </Text>
        <Datepicker
          ref={_datePicker}
          placement='top'
          date={resetDate}
          min={new Date('2020-01-01')}
          max={new Date('2029-12-31')}
          dateService={localeDateService}
          onSelect={(date) => {
            setResetDate(date)
            _datePicker.current.blur()
          }}
          style={styles.datePicker}
        />

        <View style={styles.warningCard}>
          <Text category='h6' style={styles.warningTitle}>
            ⚠️ {t('organizationDetails.warning')}
          </Text>
          <Text style={styles.warningText}>
            {t('organizationDetails.resetDataWarning')}
          </Text>
        </View>

        {/* Additional Options */}
        <View style={styles.optionsCard}>
          <Text category='h6' style={styles.optionsTitle}>
            {t('organizationDetails.additionalOptions')}
          </Text>
          
          <View style={styles.toggleItem}>
            <Toggle
              checked={shouldResetProductStocks}
              onChange={() => setShouldResetProductStocks(!shouldResetProductStocks)}
              style={styles.toggleStyle}
            >
              {t('organizationDetails.resetProductStocks')}
            </Toggle>
            <Text style={styles.toggleDescription}>
              {t('organizationDetails.resetProductStocksDescription')}
            </Text>
          </View>

          <View style={styles.toggleItem}>
          <Toggle
              checked={deleteProducts}
              onChange={() => setDeleteProducts(!deleteProducts)}
            style={styles.toggleStyle}
          >
              {t('organizationDetails.deleteAllProducts')}
          </Toggle>
            <Text style={styles.toggleDescription}>
              {t('organizationDetails.deleteAllProductsDescription')}
            </Text>
          </View>
        </View>

        <View style={[styles.resetInfoCard, { backgroundColor: theme['background-basic-color-2'], borderColor: theme['border-basic-color-3'] }]}>
          <Text category='h6' style={[styles.infoTitle, { color: theme['text-hint-color'] }]}>
            {t('organizationDetails.whatWillBeDeleted')}
          </Text>
          <Text style={[styles.infoText, { color: theme['text-hint-color'] }]}>
            • {t('sales')} {t('organizationDetails.beforeDate')} {format(resetDate, 'd MMM Y')}
          </Text>
          <Text style={[styles.infoText, { color: theme['text-hint-color'] }]}>
            • {t('complexSales.complexSales')} {t('organizationDetails.beforeDate')} {format(resetDate, 'd MMM Y')}
          </Text>
          <Text style={[styles.infoText, { color: theme['text-hint-color'] }]}>
            • {t('supplies.supplies')} {t('organizationDetails.beforeDate')} {format(resetDate, 'd MMM Y')}
          </Text>
          <Text style={[styles.infoText, { color: theme['text-hint-color'] }]}>
            • {t('writeOffs.writeOffs')} {t('organizationDetails.beforeDate')} {format(resetDate, 'd MMM Y')}
          </Text>
          <Text style={[styles.infoText, { color: theme['text-hint-color'] }]}>
            • {t('transfers.transfers')} {t('organizationDetails.beforeDate')} {format(resetDate, 'd MMM Y')}
          </Text>
          <Text style={[styles.infoText, { color: theme['text-hint-color'] }]}>
            • {t('spendings.spendings')} {t('organizationDetails.beforeDate')} {format(resetDate, 'd MMM Y')}
          </Text>
          <Text style={[styles.infoText, { color: theme['text-hint-color'] }]}>
            • {t('revisions.revisions')} {t('organizationDetails.beforeDate')} {format(resetDate, 'd MMM Y')}
          </Text>
          <Text style={[styles.infoText, { color: theme['text-hint-color'] }]}>
            • {t('manufactures.manufacture')} {t('organizationDetails.beforeDate')} {format(resetDate, 'd MMM Y')}
          </Text>
          <Text style={[styles.infoText, { color: theme['text-hint-color'] }]}>
            • {t('organizationDetails.dailyStats')} {t('organizationDetails.beforeDate')} {format(resetDate, 'd MMM Y')}
          </Text>
          <Text style={[styles.infoText, { color: theme['text-hint-color'] }]}>
            • {t('organizationDetails.monthlyStats')} {t('organizationDetails.beforeDate')} {format(resetDate, 'd MMM Y')}
          </Text>
          <Text style={[styles.infoText, { color: theme['text-hint-color'] }]}>
            • {t('organizationDetails.dailyProductReports')} {t('organizationDetails.beforeDate')} {format(resetDate, 'd MMM Y')}
          </Text>
          <Text style={[styles.infoText, { color: theme['text-hint-color'] }]}>
            • {t('organizationDetails.transactions')} {t('organizationDetails.beforeDate')} {format(resetDate, 'd MMM Y')}
          </Text>
          <Text style={[styles.infoText, { color: theme['text-hint-color'] }]}>
            • {t('organizationDetails.productStockSnapshots')} {t('organizationDetails.beforeDate')} {format(resetDate, 'd MMM Y')}
          </Text>
          <Text style={[styles.infoText, { color: theme['text-hint-color'] }]}>
            • {t('organizationDetails.orders')} {t('organizationDetails.beforeDate')} {format(resetDate, 'd MMM Y')}
          </Text>
          <Text style={[styles.infoText, { color: theme['text-hint-color'] }]}>
            • {t('organizationDetails.movementData')} {t('organizationDetails.beforeDate')} {format(resetDate, 'd MMM Y')}
          </Text>
          {shouldResetProductStocks && (
            <Text style={[styles.infoText, { color: theme['text-hint-color'] }]}>
              • {t('organizationDetails.allProductStocks')}
            </Text>
          )}
          {deleteProducts && (
            <Text style={[styles.infoText, { color: theme['text-hint-color'] }]}>
              • {t('organizationDetails.allProducts')}
            </Text>
          )}
        </View>

                {/* Reset Button */}
        <Button 
          status='danger' 
          onPress={() => setConfirmationModalVisible(true)} 
          style={styles.submitButton}
          disabled={context.user.role !== 'admin'}
        >
          {t('organizationDetails.resetAllData')}
        </Button>

        {/* Confirmation Modal for Reset Operations */}
        <Modal style={[styles.modal, { backgroundColor: theme['background-basic-color-1'] }]} 
          visible={confirmationModalVisible} 
          backdropStyle={styles.backdrop} 
          onBackdropPress={() => {
            if (!isLoading && !successMessageVisible) {
              setConfirmationModalVisible(false)
            }
          }}
        >
          <ScrollView contentContainerStyle={styles.modalPadding}>
          {!isLoading && !successMessageVisible && (
            <TouchableOpacity 
              style={{position: 'absolute', top: 10, right: 10, alignSelf: 'flex-end', alignItems: 'center', justifyContent: 'center'}} 
              onPress={() => setConfirmationModalVisible(false)}
            > 
              <Icon name='close-circle' width={32} height={32} fill={theme['text-basic-color']} />
            </TouchableOpacity>
          )}
          
          <Text category='h6' style={styles.modalTitle}>
            {t('organizationDetails.confirmResetData')}
          </Text>
          
          <Text style={styles.modalDescription}>
            {t('organizationDetails.confirmResetDataDescription')}
          </Text>
          
          <Text category='s1' style={styles.dateText}>
            {t('organizationDetails.resetDataBeforeDate')}: {format(resetDate, 'd MMM Y')}
            </Text>
          
          <View style={styles.warningCard}>
            <Text category='h6' style={styles.warningTitle}>
              ⚠️ {t('organizationDetails.warning')}
            </Text>
            <Text style={styles.warningText}>
              {t('organizationDetails.resetDataWarning')}
                </Text>
          </View>
          
          {isLoading ? (
            <View style={styles.spinner}><Spinner /></View>
          ) : successMessageVisible ? (
            <>
              <Text status='success' style={styles.label}>
                {t('success')}
              </Text>
              <Button onPress={() => {
                setConfirmationModalVisible(false)
                setSuccessMessageVisible(false)
                navigation.goBack()
              }} style={styles.submitButton}>
                {t('back')}
              </Button>
            </>
          ) : (
            <>
              {saveError != '' && (
                <Text category='p1' status='danger' style={styles.label}>
                  {saveError}
                </Text>
              )}
              <Button status='danger' onPress={executeResetOperations} style={styles.submitButton}>
                {t('organizationDetails.confirmReset')}
              </Button>
            </>
          )}
          </ScrollView>
        </Modal>

        <View style={styles.bottomSpacer}></View>
      </ScrollView>
    </KeyboardAvoidingView>
  )
}

const styles = StyleSheet.create({
  keyboardAvoidingView: {
    flex: 1,
  },
  container: {
    flex: 1,
    padding: 20,
  },
  title: {
    marginBottom: 5,
  },
  description: {
    marginBottom: 20,
  },
  adminRequiredCard: {
    backgroundColor: '#ffebee',
    borderColor: '#ffcdd2',
    borderWidth: 1,
    borderRadius: 10,
    padding: 15,
    marginBottom: 20,
  },
  adminRequiredTitle: {
    color: '#c62828',
    marginBottom: 8,
  },
  adminRequiredText: {
    color: '#c62828',
    fontSize: 14,
    lineHeight: 18,
  },
  dateLabel: {
    marginBottom: 10,
  },
  datePicker: {
    marginBottom: 20,
  },
  warningCard: {
    backgroundColor: '#fff3cd',
    borderColor: '#ffeaa7',
    borderWidth: 1,
    borderRadius: 10,
    padding: 15,
    marginBottom: 20,
  },
  warningTitle: {
    color: '#856404',
    marginBottom: 8,
  },
  warningText: {
    color: '#856404',
    fontSize: 14,
    lineHeight: 18,
  },
  optionsCard: {
    backgroundColor: '#e3f2fd',
    borderColor: '#bbdefb',
    borderWidth: 1,
    borderRadius: 10,
    padding: 15,
    marginBottom: 20,
  },
  optionsTitle: {
    marginBottom: 15,
    color: '#1565c0',
  },
  toggleItem: {
    marginBottom: 15,
  },
  toggleStyle: {
    alignSelf: 'flex-start',
    marginBottom: 5,
  },
  toggleDescription: {
    fontSize: 14,
    lineHeight: 18,
    color: '#1565c0',
  },
  resetInfoCard: {
    borderWidth: 1,
    borderRadius: 10,
    padding: 15,
    marginBottom: 20,
  },
  infoTitle: {
    marginBottom: 10,
  },
  infoText: {
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 4,
  },
  label: {
    marginBottom: 8,
    marginTop: 16,
  },
  submitButton: {
    marginTop: 16,
  },
  spinner: {
    marginTop: 15,
    alignItems: 'center'
  },
  modal: {
    top: 100,
    width: '80%',
    height: '80%',
    borderRadius: 20,
  },
  backdrop: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  modalPadding: {
    padding: 32
  },
  modalTitle: {
    marginTop: 20,
    marginBottom: 12,
  },
  modalDescription: {
    marginBottom: 20,
    fontSize: 14,
  },
  dateText: {
    fontSize: 14,
    marginBottom: 20,
    fontWeight: 'bold',
  },
  bottomSpacer: {
    height: 100,
  },
});